import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "wouter";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { accountApi } from "@/services/api-client";
import { ErrorBox, errorMessage } from "@/features/onboarding/shared";
import { AuthLayout } from "./auth-layout";

type Mode = "verify-email" | "forgot-password" | "reset-password";

/** Reads challenge_id/token once, then strips them from the address bar. Kept in memory only. */
function useSignedParams() {
  const ref = useRef<{
    challenge_id: string;
    expires: number;
    signature: string;
    email: string;
  } | null>(null);
  if (ref.current === null) {
    const q = new URLSearchParams(window.location.search);
    ref.current = {
      challenge_id: q.get("challenge_id") ?? q.get("challenge") ?? "",
      expires: Number(q.get("expires") ?? 0),
      signature: q.get("signature") ?? q.get("token") ?? "",
      email: q.get("email") ?? "",
    };
  }
  useEffect(() => {
    if (window.location.search)
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname + window.location.hash,
      );
  }, []);
  return ref.current;
}

const COPY: Record<Mode, [string, string]> = {
  "verify-email": [
    "Vérification e-mail",
    "Confirmez votre adresse pour sécuriser votre espace.",
  ],
  "forgot-password": ["Récupération", "Récupérez l'accès à votre espace."],
  "reset-password": [
    "Nouveau mot de passe",
    "Choisissez un nouveau mot de passe robuste.",
  ],
};

export default function AuthActionsPage({ mode }: { mode: Mode }) {
  const signed = useSignedParams();
  const [email, setEmail] = useState(signed.email);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const verifyStarted = useRef(false);
  const hasSigned = Boolean(
    signed.challenge_id && signed.expires > 0 && signed.signature,
  );

  const verify = async () => {
    setBusy(true);
    setError("");
    try {
      await accountApi("/auth/email/verify", {
        method: "POST",
        body: {
          challenge_id: signed.challenge_id,
          expires: signed.expires,
          signature: signed.signature,
        },
      });
      setDone("Votre adresse e-mail est vérifiée. Vous pouvez vous connecter.");
    } catch (c) {
      setError(errorMessage(c, "Lien invalide ou expiré."));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (mode === "verify-email" && hasSigned && !verifyStarted.current) {
      verifyStarted.current = true;
      void verify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, hasSigned]);

  const resend = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await accountApi("/auth/email/verification/resend", {
        method: "POST",
        body: { email: email.trim() },
      });
      setDone(
        "Si un compte correspond, un nouvel e-mail de vérification a été envoyé.",
      );
    } catch (c) {
      setError(errorMessage(c));
    } finally {
      setBusy(false);
    }
  };

  const forgot = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await accountApi("/auth/password/forgot", {
        method: "POST",
        body: { email: email.trim() },
      });
      setDone(
        "Si un compte correspond à cette adresse, un lien de réinitialisation a été envoyé.",
      );
    } catch (c) {
      setError(errorMessage(c));
    } finally {
      setBusy(false);
    }
  };

  const reset = async (e: FormEvent) => {
    e.preventDefault();
    if (pw.length < 10)
      return setError("Le mot de passe doit contenir au moins 10 caractères.");
    if (pw !== pw2) return setError("Les mots de passe ne correspondent pas.");
    setBusy(true);
    setError("");
    try {
      await accountApi("/auth/password/reset", {
        method: "POST",
        body: {
          challenge_id: signed.challenge_id,
          expires: signed.expires,
          signature: signed.signature,
          password: pw,
          password_confirmation: pw2,
        },
      });
      setPw("");
      setPw2("");
      setDone(
        "Mot de passe réinitialisé. Toutes vos sessions ont été fermées.",
      );
    } catch (c) {
      setError(errorMessage(c, "Lien invalide ou expiré."));
    } finally {
      setBusy(false);
    }
  };

  const [badge, sub] = COPY[mode];
  const emailField = (
    <div className="space-y-2">
      <Label htmlFor="act-email">Adresse e-mail</Label>
      <Input
        id="act-email"
        type="email"
        autoComplete="email"
        data-testid="input-action-email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
    </div>
  );
  const submitBtn = (label: string, id: string) => (
    <Button
      type="submit"
      className="h-11 w-full"
      disabled={busy}
      data-testid={id}
    >
      {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
      {label}
    </Button>
  );

  return (
    <AuthLayout badge={badge} title={sub}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
        {badge}
      </p>
      <div className="mt-6 space-y-5">
        {error && <ErrorBox message={error} testId="error-auth-action" />}
        {done ? (
          <div
            className="rounded-2xl border bg-card p-6"
            data-testid="text-action-done"
          >
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            <p className="mt-3 text-sm font-semibold">{done}</p>
          </div>
        ) : mode === "verify-email" ? (
          hasSigned && (busy || !error) ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Vérification en cours...
            </div>
          ) : (
            <form
              onSubmit={resend}
              className="space-y-4"
              data-testid="form-resend-verification"
            >
              <p className="text-sm text-muted-foreground">
                Renvoyez un e-mail de vérification.
              </p>
              {emailField}
              {submitBtn("Renvoyer l'e-mail", "button-resend-verification")}
            </form>
          )
        ) : mode === "forgot-password" ? (
          <form
            onSubmit={forgot}
            className="space-y-4"
            data-testid="form-forgot-password"
          >
            {emailField}
            {submitBtn("Envoyer le lien", "button-forgot-password")}
          </form>
        ) : !hasSigned ? (
          <ErrorBox message="Lien de réinitialisation incomplet. Demandez un nouveau lien." />
        ) : (
          <form
            onSubmit={reset}
            className="space-y-4"
            data-testid="form-reset-password"
          >
            <div className="space-y-2">
              <Label htmlFor="pw">Nouveau mot de passe</Label>
              <Input
                id="pw"
                type="password"
                autoComplete="new-password"
                data-testid="input-new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw2">Confirmation</Label>
              <Input
                id="pw2"
                type="password"
                autoComplete="new-password"
                data-testid="input-confirm-password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
              />
            </div>
            {submitBtn("Réinitialiser", "button-reset-password")}
          </form>
        )}
        <div className="flex justify-between text-sm">
          <Link
            href="/login"
            className="font-semibold text-primary hover:underline"
          >
            Se connecter
          </Link>
          {mode === "reset-password" && (
            <Link
              href="/forgot-password"
              className="text-primary hover:underline"
            >
              Nouveau lien
            </Link>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}
